import * as H from "history";
import * as _ from "lodash";
import * as React from "react";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";

import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import useLocalStorage from "src/library/hooks/useLocalStorage";
import { MenuType } from "src/utils/menu/menus";
import { IStore } from "src/core/shared/store";
import { setGlobalError } from "src/core/shared/store/global/global.actions";
import useCanAccessRoute from "src/core/shared/hooks/menu/useCanAccessRoute";
import {
  IRoute,
  Routes as allRoutes
} from "src/pages/Router/routes";
import useCurrentUser from "src/core/users/hooks/useCurrentUser";

import useBeforeRemoveContext from "./useBeforeRemoveContext";
import useContextToMenuContext from "./useContextToMenuContext";
import useGetMenuItemByPath from "./useGetMenuItemByPath";

export const MenuContext = React.createContext<[MenuContext, MenuApi]>([
  {
    routes: [],
    context: [],
    currentRoute: null,
    menuType: "NONE"
  },
  {
    removeParent: () => null,
    clickMenuItem: () => null,
    setMenuType: () => null,
    handleRouteLoad: () => null,
    removeCurrentContext: () => null,
    setContextByType: () => null
  }
]);
interface MenuContext extends MenuState {
  routes: IRoute[];
}
interface MenuState {
  menuType: MenuType;
  context: MenuContextType[];
  currentRoute: IRoute | null;
}
interface MenuProviderProps {
  terminology: ITerminology;
  children: React.ReactChild;
  history: H.History;
}

/**
 * Menu provider
 */
export const MenuProvider: React.ReactFCWithChildren<MenuProviderProps> = props => {
  const dispatch = useDispatch();

  const {
    global: { role, error: globalError },
    currentOrder,
    currentList
  } = useMappedState(({
    global, orders, lists
  }: IStore) => ({
    global,
    currentOrder: orders.currentOrder,
    currentList: lists.currentList
  }));

  const [ { currentRole } ] = useCurrentUser();
  const canAccessRoute = useCanAccessRoute();
  const getItemByPath = useGetMenuItemByPath();
  const beforeRemoveContext = useBeforeRemoveContext();
  const contextToMenuContext = useContextToMenuContext();
  const [ initialContext, saveContext ] = useLocalStorage("context");
  const [ currentRoute, setCurrentRoute ] = React.useState<IRoute | null>(null);
  const [ context, setContextState ] = React.useState<MenuContextType[]>([]);
  const [ menuType, setMenuType ] = React.useState<MenuType>(role && role.type === "admin" ? "ADMIN_MAIN" : "MAIN");

  React.useEffect(() => {
    if (initialContext.length) {
      try {
        const initialContextState = JSON.parse(initialContext);

        setContext(initialContextState);
      } catch (error) {
        console.error("Tried to load invalid context");
      }
    }
  }, []);

  const setContext = React.useCallback((newContext: MenuContextType[]) => {
    setContextState(newContext);
    saveContext(JSON.stringify(newContext));
  }, []);

  const routes = React.useMemo(() => {
    return _.filter(allRoutes, route => canAccessRoute(route));
  }, [ canAccessRoute ]);

  const setCurrent = React.useCallback(
    (path: string) => {
      const currentRoute = getItemByPath(path, routes);

      if (currentRoute) {
        setCurrentRoute(currentRoute);
      }

      return currentRoute || null;
    },
    [ getItemByPath, routes ]
  );

  const handleRouteLoad = React.useCallback(
    (path: string) => {
      if (path !== "/maintenance") {
        if (globalError) {
          dispatch(setGlobalError(null));
        }

        if (routes.length) {
          const route = setCurrent(path);

          if (route) {
            setMenuType(route.menuType);

            if (context.length && route.context !== context[ 0 ].type) {
              for (const currentContextIndex in context) {
                const currentContext = context[ currentContextIndex ];

                if (currentContext && (!route.allowedContexts || !route.allowedContexts.includes(currentContext.type))) {
                  // handle any actions fo this context before removing it
                  if (beforeRemoveContext(currentContext.type, routes)) {
                    context.splice(parseInt(currentContextIndex, 10), 1);

                    if (currentContext.onRemove) {
                      currentContext.onRemove();
                    }
                  }
                }
              }
            }

            if (route.context && (!context[ 0 ] || route.context !== context[ 0 ].type)) {
              const newContext = contextToMenuContext(route.context, context, routes);

              if (newContext) {
                if (_.findIndex(context, cont => cont.type === newContext.type) === -1) {
                  context.unshift(newContext);
                }
              }
            }

            setContext([ ...context ]);
          }
        }
      }
    },
    [
      globalError,
      routes,
      dispatch,
      setCurrent,
      context,
      beforeRemoveContext,
      contextToMenuContext,
      setContext
    ]
  );

  const removeCurrentContext = React.useCallback(
    (force = false, redirect = true) => {
      const toRemove = context[ 0 ];

      if (toRemove && beforeRemoveContext(toRemove.type, routes, force)) {
        context.splice(0, 1);
        setContext([ ...context ]);

        if (redirect) {
          props.history.push(toRemove.origin);
        }
      }
    },
    [
      context,
      props.history,
      setContext,
      beforeRemoveContext,
      routes
    ]
  );

  const setContextByType = React.useCallback(
    (contextType: ContextType) => {
      const newContext = contextToMenuContext(contextType, context, routes);

      if (newContext) {
        if (!context.length || context[ 0 ].type !== contextType) {
          context.unshift(newContext);
          setContext([ ...context ]);
        }
      }
    },
    [
      context,
      contextToMenuContext,
      setContext,
      routes
    ]
  );

  const clickMenuItem = React.useCallback(
    (item: IRoute) => {
      setMenuType(item.menuType as MenuType);

      if (item.loadedPath) {
        props.history.push(item.loadedPath);
      } else if (item.path) {
        props.history.push(item.path);
      }
    },
    [ props.history, setMenuType ]
  );

  const removeParent = React.useCallback(
    (parent: IRoute) => {
      if (parent) {
        if (parent.path) {
          props.history.push(parent.path);
        }
      }
    },
    [ props.history ]
  );

  // restrict van sales role to certain routes only
  React.useEffect(() => {
    if (currentRole?.code === "van-sales-admin") {
      const allowedRoutes = [
        "/orders/:orderId",
        "/products",
        "/products/:productId",
        "/van-sales",
        "/van-sales/orders/:orderId/order-placed"
      ];

      if (currentRoute?.path && !allowedRoutes.includes(currentRoute?.path)) {
        props.history.push("/van-sales");
      }
    }
  }, [
    currentRole,
    currentRoute,
    props.history
  ]);

  React.useEffect(() => {
    // give the route time to load first

    if (currentRoute) {
      if (
        currentRoute.allowedContexts &&
        (currentRoute.allowedContexts.includes("ORDER_PRODUCT_SELECTION") || currentRoute.allowedContexts.includes("LIST_PRODUCT_SELECTION"))
      ) {
        if (currentOrder.mode === "edit") {
          setContextByType("ORDER_PRODUCT_SELECTION");
        } else if (context[ 0 ] && context[ 0 ].type === "ORDER_PRODUCT_SELECTION") {
          if (!currentOrder.saved && currentOrder.data) {
            props.history.push(`/orders/${currentOrder.data.id}`);
            beforeRemoveContext("ORDER_PRODUCT_SELECTION", routes, false);
          } else {
            removeCurrentContext(true, false);
          }
        }

        if (currentList.mode === "edit") {
          setContextByType("LIST_PRODUCT_SELECTION");
        } else if (context[ 0 ] && context[ 0 ].type === "LIST_PRODUCT_SELECTION") {
          if (!currentList.saved && currentList.data) {
            props.history.push(`/lists/${currentList.data.id}`);
            beforeRemoveContext("LIST_PRODUCT_SELECTION", routes, false);
          } else {
            removeCurrentContext(true, false);
          }
        }
      } else {
        // no context in menu
        if (currentList.data) {
          if (!currentList.saved) {
            const hasListContext = _.find(context, c => c.type === "LIST_PRODUCT_SELECTION");

            if (hasListContext) {
              props.history.push(`/lists/${currentList.data.id}`);
              beforeRemoveContext("LIST_PRODUCT_SELECTION", routes, false);
            }
          }
        }

        if (currentOrder.data) {
          if (!currentOrder.saved) {
            const hasOrderContext = _.find(context, c => c.type === "ORDER_PRODUCT_SELECTION");

            if (hasOrderContext) {
              props.history.push(`/orders/${currentOrder.data.id}`);
              beforeRemoveContext("ORDER_PRODUCT_SELECTION", routes, false);
            }
          }
        }
      }
    }
  }, [
    context.length,
    currentRoute,
    currentOrder.mode,
    currentList.mode,
    beforeRemoveContext,
    currentOrder.saved,
    currentOrder.data,
    context,
    currentList.saved,
    currentList.data,
    setContextByType,
    props.history,
    routes,
    removeCurrentContext
  ]);

  return (
    <MenuContext.Provider
      value={[
        {
          currentRoute,
          context,
          routes,
          menuType
        },
        {
          removeParent,
          clickMenuItem,
          setMenuType,
          handleRouteLoad,
          removeCurrentContext,
          setContextByType
        }
      ]}
    >
      {props.children}
    </MenuContext.Provider>
  );
};

export type ContextType =
  | "ORDER"
  | "CREATE_EDIT_USER"
  | "CREATE_EDIT_USER_GROUP"
  | "CREATE_EDIT_CUSTOMER"
  | "CREATE_EDIT_CUSTOMER_USER"
  | "CREATE_EDIT_DROP"
  | "CREATE_EDIT_NOTIFICATION"
  | "CREATE_EDIT_PRODUCT"
  | "CREATE_EDIT_PROMOTION"
  | "CREATE_EDIT_PROMOTIONAL_TAG"
  | "PLATFORM_SETTINGS"
  | "ORDER_PRODUCT_SELECTION"
  | "CREATE_EDIT_DELIVERY_ROUTE"
  | "LIST_PRODUCT_SELECTION"
  | "CREATE_EDIT_PRICE_CODE"
  | "LIST_USERS";

export interface ISideBarContext<Type = any, Data = any> {
    type: Type;
    title: string;
    origin: string;
    data?: Data;
}
export type MenuContextType<T = any> = ISideBarContext<ContextType, T> & { parent?: IRoute; onRemove?: () => void };

export interface MenuApi {
  removeParent: (parent: IRoute) => void;
  clickMenuItem: (item: IRoute) => void;
  setMenuType: (type: MenuType) => void;
  handleRouteLoad: (path: string) => void;
  removeCurrentContext: (force?: boolean) => void;
  setContextByType: (contextType: ContextType) => void;
}
